/**
 * ActionCardShell - Unified card shell for the v2 dashboard redesign.
 *
 * Always-open card with a header row (checkbox, title, edit button) and
 * content area. No accordion collapse — content is always visible.
 *
 * The Edit button appears for editable actions and toggles the content
 * between read-only and edit modes.
 */

import type { ReactNode } from 'react';
import { useId, useMemo } from 'react';

import { cx } from '@/utils/cx';

import { ActionCheckbox } from './ActionCheckbox';
import { EditActionToggle } from './EditActionToggle';
import { DetailPanelCard } from './shared/DetailPanelCard';
import {
  type ActionVariant,
  VARIANT_BORDER,
  VARIANT_HEADER_BG,
} from './shared/variants';

export interface ActionCardShellProps {
  /** Visual variant of the card */
  variant?: ActionVariant;
  /** Title of the action */
  title: string;
  /** Execution order (1-based for display). Hidden when undefined. */
  executionOrder?: number;
  /** Whether to show the selection checkbox */
  showCheckbox: boolean;
  /** Whether the checkbox is checked */
  isSelected: boolean;
  /** Callback when checkbox is toggled */
  onToggleSelection: () => void;
  /** Whether the checkbox is disabled */
  isCheckboxDisabled?: boolean;
  /** Reason the checkbox is disabled (shown in tooltip) */
  disabledReason?: string;
  /** Whether to show the Edit button */
  showEditButton: boolean;
  /** Whether the card is in edit mode */
  isEditing: boolean;
  /** Callback to toggle edit mode */
  onToggleEdit: () => void;
  /** Slot for right side of header (e.g. "Ready to send" badge, Gmail icon) */
  headerRight?: ReactNode;
  /** Accessible label for the card article */
  ariaLabel?: string;
  /** Content area — always rendered */
  children?: ReactNode;
  /** Additional CSS classes on the outer container */
  className?: string;
  // ── Edit-mode footer props (only used when isEditing) ───────────────────
  /** Whether the edited content differs from the original */
  isDirty?: boolean;
  /** Whether a save operation is in progress */
  isSaving?: boolean;
  /** Callback when Save button is clicked */
  onSave?: () => void;
  /** Callback when Cancel button is clicked */
  onCancel?: () => void;
}

/**
 * Always-open card shell for v2 action cards.
 *
 * Header shows checkbox (optional), numbered title, edit button (for editable actions),
 * and a right slot. Content is always visible below the header.
 */
export function ActionCardShell({
  variant = 'default',
  title,
  executionOrder,
  showCheckbox,
  isSelected,
  onToggleSelection,
  isCheckboxDisabled = false,
  disabledReason,
  showEditButton,
  isEditing,
  onToggleEdit,
  headerRight,
  ariaLabel,
  children,
  className,
  isDirty = false,
  isSaving = false,
  onSave,
  onCancel,
}: ActionCardShellProps) {
  const disabledDescriptionId = useId();

  // Prefix used by edit-button aria labels: "1. Send email" or just "Send email".
  const editLabelSubject = useMemo(
    () => (executionOrder != null ? `${executionOrder}. ${title}` : title),
    [executionOrder, title]
  );
  const editAriaLabel = isEditing
    ? `Cancel editing ${editLabelSubject}`
    : `Edit ${editLabelSubject}`;

  return (
    <DetailPanelCard className={cx(VARIANT_BORDER[variant], className)}>
      <div
        role='article'
        aria-label={
          ariaLabel ?? (executionOrder ? `${executionOrder}. ${title}` : title)
        }
      >
        {/* Header row — static, not clickable */}
        <div
          className={cx(
            'w-full px-4 py-3 flex items-center gap-3',
            VARIANT_HEADER_BG[variant]
          )}
        >
          {/* Checkbox (left) */}
          {showCheckbox && (
            <div className='shrink-0'>
              <ActionCheckbox
                checked={isSelected}
                disabled={isCheckboxDisabled}
                onChange={onToggleSelection}
                disabledReason={disabledReason}
                ariaLabel={
                  executionOrder != null
                    ? `Select action ${executionOrder}`
                    : 'Select action'
                }
                aria-describedby={
                  isCheckboxDisabled && disabledReason
                    ? disabledDescriptionId
                    : undefined
                }
              />
              {/* Visually hidden description for screen readers when disabled */}
              {isCheckboxDisabled && disabledReason && (
                <span id={disabledDescriptionId} className='sr-only'>
                  {disabledReason}
                </span>
              )}
            </div>
          )}

          {/* Title (center-left) */}
          <span className='flex-1 font-semibold text-sm text-fg-primary truncate'>
            {executionOrder != null && `${executionOrder}. `}
            {title}
          </span>

          {/* Right slot (badges, icons) */}
          {headerRight && <div className='shrink-0'>{headerRight}</div>}

          {/* Edit button — far right */}
          {showEditButton && (
            <EditActionToggle
              isEditing={isEditing}
              onToggle={onToggleEdit}
              ariaLabel={editAriaLabel}
            />
          )}
        </div>

        {/* Content area — always visible */}
        {children && (
          <div className='border-t border-border-secondary'>{children}</div>
        )}

        {/* Edit-mode footer — Save / Cancel buttons */}
        {isEditing && (onSave !== undefined || onCancel !== undefined) && (
          <div className='border-t border-border-secondary px-4 py-2 flex justify-end gap-2'>
            {onCancel !== undefined && (
              <button
                type='button'
                aria-label={title ? `Cancel editing ${title}` : 'Cancel'}
                onClick={onCancel}
                disabled={isSaving}
                className='px-3 py-1.5 text-sm font-medium text-fg-secondary bg-bg-secondary border border-border-primary rounded-lg hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                Cancel
              </button>
            )}
            {onSave !== undefined && (
              <button
                type='button'
                aria-label={title ? `Save changes to ${title}` : 'Save changes'}
                onClick={onSave}
                disabled={!isDirty || isSaving}
                className='px-3 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        )}
      </div>
    </DetailPanelCard>
  );
}
